import React from 'react';
import { motion } from 'framer-motion';
import { useCommunityStats } from '@/hooks/useCommunityStats';

/**
 * Early-community social proof for the paywall.
 *
 * Framed as "you'd be one of the first N" rather than "join N others",
 * because at the size this community is today the second reading invites the
 * question the first one answers. The copy holds up as the number grows —
 * nothing here has to be rewritten at 1,200.
 *
 * Two deliberate choices:
 *
 *   No faces. Avatar stacks on paywalls are almost always stock photos of
 *   people who never used the product, which is a fabricated endorsement.
 *   These are abstract marks that stand for members without pretending to
 *   depict them.
 *
 *   No number, no block. `husbands` comes from the live endpoint with no
 *   hardcoded fallback, and this renders nothing at all until it arrives.
 *   An invented or stale count beside a Buy button is worse than silence.
 */
const SocialProof = ({ lang = 'en', className = '', showGestures = false }) => {
  const { husbands, gestures } = useCommunityStats();

  if (!husbands) return null;

  const locale = lang === 'it' ? 'it-IT' : lang === 'es' ? 'es-ES' : 'en-US';
  const formatted = husbands.toLocaleString(locale);

  const headline = lang === 'it'
    ? <>Saresti uno dei primi <strong className="font-bold">{formatted}</strong> mariti a costruire questa abitudine.</>
    : lang === 'es'
    ? <>Serías uno de los primeros <strong className="font-bold">{formatted}</strong> maridos construyendo este hábito.</>
    : <>You'd be one of the first <strong className="font-bold">{formatted}</strong> husbands building this habit.</>;

  // Off by default, and deliberately so: today the gesture count is lower
  // than the headcount, so showing both invites the arithmetic and answers it
  // badly — it reads as "most of them aren't doing anything". The headline
  // stands on its own. Turn this on once the figure is comfortably larger
  // than the number of members, where it starts to add weight instead of
  // taking it away.
  const gesturesLine = showGestures && gestures
    ? (lang === 'it'
        ? `${gestures.toLocaleString(locale)} gesti completati finora`
        : lang === 'es'
        ? `${gestures.toLocaleString(locale)} gestos completados hasta ahora`
        : `${gestures.toLocaleString(locale)} gestures completed so far`)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-left ${className}`}
      style={{ background: 'rgba(109,179,63,0.08)', border: '1px solid rgba(109,179,63,0.25)' }}
    >
      {/* Abstract member marks — three overlapping rings, not faces. */}
      <div className="flex shrink-0" aria-hidden="true">
        {['#6DB33F', '#F5A623', '#3B82F6'].map((color, i) => (
          <span
            key={color}
            className="w-6 h-6 rounded-full"
            style={{
              background: `${color}26`,
              border: `1.5px solid ${color}`,
              marginLeft: i === 0 ? 0 : -8,
              zIndex: 3 - i,
            }}
          />
        ))}
      </div>

      <div className="min-w-0">
        <p className="text-[12.5px] leading-snug text-[#1A211E]">{headline}</p>
        {gesturesLine && (
          <p className="text-[11px] leading-snug text-[rgba(26,33,30,0.68)] mt-0.5">
            {gesturesLine}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default SocialProof;
